import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { ArrowLeft, CheckCircle2, X } from 'lucide-react';
import { formatPhoneNumberInput } from '../phone-number.js';
import { validateChangeMessage, validateContactDetails, validateCustomerEmail, validateCustomerName, validateCustomerPhone, type ChangeRequestErrors, type ChangeRequestField } from '../change-request-validation.js';

export type ChangeRequestSubmission = {
  message: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
};

export function ActionModal({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  if (!open) return null;
  return <div className="dialog-backdrop centered" role="presentation" onMouseDown={event => { if (event.currentTarget === event.target) onClose(); }}>
    <section className="action-modal" role="dialog" aria-modal="true" aria-labelledby="action-modal-title">
      <header><h2 id="action-modal-title">{title}</h2><button ref={closeRef} type="button" onClick={onClose} aria-label="Close"><X /></button></header>{children}
    </section>
  </div>;
}

export function ChangeRequestModal({ open, onClose, onSubmit }: { open: boolean; onClose: () => void; onSubmit: (request: ChangeRequestSubmission) => Promise<void> }) {
  const [step, setStep] = useState<'changes' | 'contact' | 'sent'>('changes');
  const [message, setMessage] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<ChangeRequestErrors>({});

  useEffect(() => {
    if (open) return;
    setStep('changes');
    setMessage('');
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setSubmitting(false);
    setError('');
    setFieldErrors({});
  }, [open]);

  const setFieldError=(field:ChangeRequestField,message:string)=>setFieldErrors(current=>{
    if(current[field]===message)return current;
    const next={...current};
    if(message)next[field]=message;else delete next[field];
    return next;
  });
  const focusField=(id:string)=>window.requestAnimationFrame(()=>document.getElementById(id)?.focus());

  const continueToContact = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const nextMessage=String(data.get('message') ?? '').trim();
    const messageError=validateChangeMessage(nextMessage);
    if(messageError){
      setFieldError('message',messageError);
      focusField('change-message');
      return;
    }
    setMessage(nextMessage);
    setFieldErrors({});
    setError('');
    setStep('contact');
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const contact={customerName:customerName.trim(),customerEmail:customerEmail.trim(),customerPhone:customerPhone.trim()};
    const validationErrors=validateContactDetails(contact);
    if(Object.keys(validationErrors).length){
      setFieldErrors(validationErrors);
      const firstField=(['customerName','customerEmail','customerPhone'] as const).find(field=>validationErrors[field]);
      if(firstField)focusField(`change-${firstField.replace(/[A-Z]/g,letter=>`-${letter.toLowerCase()}`)}`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        message,
        ...contact,
      });
      setStep('sent');
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Your request could not be sent. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  return <ActionModal open={open} onClose={onClose} title="Request changes">
    {step !== 'sent' && <div className="change-progress" aria-label={`Step ${step === 'changes' ? '1' : '2'} of 2`}><i className="active" /><i className={step === 'contact' ? 'active' : ''} /><span>Step {step === 'changes' ? '1' : '2'} of 2</span></div>}
    {step === 'changes' && <form className="change-form" onSubmit={continueToContact} noValidate>
      <div className="change-form-intro"><strong>Tell us what needs adjusting</strong><span>Be as specific as you like. You’ll add your contact details next.</span></div>
      <label htmlFor="change-message">Requested changes</label>
      <textarea id="change-message" name="message" required minLength={5} maxLength={5000} rows={6} defaultValue={message} placeholder="Tell us what you’d like adjusted…" aria-invalid={Boolean(fieldErrors.message)} aria-describedby={fieldErrors.message?'change-message-error':undefined} onBlur={event=>setFieldError('message',validateChangeMessage(event.currentTarget.value))} onInput={event=>{if(fieldErrors.message)setFieldError('message',validateChangeMessage(event.currentTarget.value));}} autoFocus />
      {fieldErrors.message&&<small id="change-message-error" className="change-field-error" role="alert">{fieldErrors.message}</small>}
      <button className="sheet-primary" type="submit">Send request</button>
    </form>}
    {step === 'contact' && <form className="change-form" onSubmit={event => void submit(event)} noValidate>
      <div className="change-form-intro"><strong>How should we reach you?</strong><span>We’ll use these details only to follow up about this request.</span></div>
      <label htmlFor="change-customer-name">Name</label>
      <input id="change-customer-name" name="customerName" type="text" required minLength={2} maxLength={100} autoComplete="name" placeholder="Your name" value={customerName} aria-invalid={Boolean(fieldErrors.customerName)} aria-describedby={fieldErrors.customerName?'change-customer-name-error':undefined} onBlur={event=>setFieldError('customerName',validateCustomerName(event.currentTarget.value))} onChange={event=>{setCustomerName(event.target.value);if(fieldErrors.customerName)setFieldError('customerName',validateCustomerName(event.target.value));}} autoFocus />
      {fieldErrors.customerName&&<small id="change-customer-name-error" className="change-field-error" role="alert">{fieldErrors.customerName}</small>}
      <label htmlFor="change-customer-email">Email</label>
      <input id="change-customer-email" name="customerEmail" type="email" required maxLength={254} autoComplete="email" placeholder="you@example.com" value={customerEmail} aria-invalid={Boolean(fieldErrors.customerEmail)} aria-describedby={fieldErrors.customerEmail?'change-customer-email-error':undefined} onBlur={event=>setFieldError('customerEmail',validateCustomerEmail(event.currentTarget.value))} onChange={event=>{setCustomerEmail(event.target.value);if(fieldErrors.customerEmail)setFieldError('customerEmail',validateCustomerEmail(event.target.value));}} />
      {fieldErrors.customerEmail&&<small id="change-customer-email-error" className="change-field-error" role="alert">{fieldErrors.customerEmail}</small>}
      <label htmlFor="change-customer-phone">Phone number</label>
      <input id="change-customer-phone" name="customerPhone" type="tel" required minLength={7} maxLength={30} autoComplete="tel" inputMode="tel" placeholder="(555) 555-0123" aria-invalid={Boolean(fieldErrors.customerPhone)} aria-describedby={`change-phone-hint${fieldErrors.customerPhone?' change-customer-phone-error':''}`} value={customerPhone} onBlur={event=>setFieldError('customerPhone',validateCustomerPhone(event.currentTarget.value))} onChange={event=>{const formatted=formatPhoneNumberInput(event.target.value);setCustomerPhone(formatted);if(fieldErrors.customerPhone)setFieldError('customerPhone',validateCustomerPhone(formatted));}} />
      <small id="change-phone-hint" className="change-field-hint">U.S. numbers format automatically. Start with + and a country code for international numbers.</small>
      {fieldErrors.customerPhone&&<small id="change-customer-phone-error" className="change-field-error" role="alert">{fieldErrors.customerPhone}</small>}
      {error && <p className="change-form-error" role="alert">{error}</p>}
      <div className="change-form-actions"><button className="change-back" type="button" onClick={() => { setError(''); setFieldErrors({}); setStep('changes'); }} disabled={submitting}><ArrowLeft />Back</button><button className="sheet-primary" type="submit" aria-busy={submitting} disabled={submitting}>{submitting ? 'Submitting…' : 'Submit change request'}</button></div>
    </form>}
    {step === 'sent' && <div className="success-message"><CheckCircle2 /><h3>Request sent</h3><p>Auri79 received your notes and contact details and will follow up shortly.</p><button className="sheet-primary" type="button" onClick={onClose}>Return to website</button></div>}
  </ActionModal>;
}
